import { AppState } from '@/types';

export interface WeeklyBonusResult {
  activityCreditsEarned: number;
  goldEarned: number;
  bonusBreakdown: {
    base: number;
    achievement: number;
    stretch: number;
    streak: number;
  };
  celebrationMessage: string;
  achievementLevel: 'missed' | 'achieved' | 'exceeded' | 'exceptional';
}

export function calculateWeeklyBonus(
  currentWeekPoints: number,
  weeklyGoal: number,
  baselineGoal: number
): WeeklyBonusResult {
  // Check if weekly goal was achieved
  const weeklyGoalAchieved = currentWeekPoints >= weeklyGoal;
  const baselineGoalAchieved = currentWeekPoints >= baselineGoal;
  const percentageOfGoal = (currentWeekPoints / weeklyGoal) * 100;
  
  if (!weeklyGoalAchieved) {
    const baselineMessage = baselineGoalAchieved 
      ? `You hit your baseline of ${baselineGoal} points! ✅` 
      : `You're ${baselineGoal - currentWeekPoints} points away from your baseline of ${baselineGoal} points.`;
    
    return {
      activityCreditsEarned: 0,
      goldEarned: 0,
      bonusBreakdown: { base: 0, achievement: 0, stretch: 0, streak: 0 },
      celebrationMessage: `You earned ${currentWeekPoints} points this week. Try for ${weeklyGoal} next week! ${baselineMessage}`,
      achievementLevel: 'missed'
    };
  }

  // Weekly bonus: 10% of weekly goal as activity credits
  const weeklyBonus = Math.floor(weeklyGoal * 0.1);
  
  // Determine achievement level and message
  let achievementLevel: 'missed' | 'achieved' | 'exceeded' | 'exceptional';
  let celebrationMessage: string;
  
  if (percentageOfGoal >= 150) {
    achievementLevel = 'exceptional';
    celebrationMessage = `🎉 EXCEPTIONAL! ${currentWeekPoints} points! You earned ${weeklyBonus} activity credits!`;
  } else if (percentageOfGoal >= 120) {
    achievementLevel = 'exceeded';
    celebrationMessage = `🚀 Goal exceeded! ${currentWeekPoints} points! You earned ${weeklyBonus} activity credits!`;
  } else {
    achievementLevel = 'achieved';
    celebrationMessage = `✅ Goal achieved! ${currentWeekPoints} points! You earned ${weeklyBonus} activity credits!`;
  }
  
  // Add baseline achievement info
  const baselineMessage = baselineGoalAchieved 
    ? `You also hit your baseline of ${baselineGoal} points! 🎉` 
    : `You're ${baselineGoal - currentWeekPoints} points away from your baseline of ${baselineGoal} points.`;
  
  celebrationMessage += ` ${baselineMessage}`;

  return {
    activityCreditsEarned: weeklyBonus,
    goldEarned: 0,
    bonusBreakdown: {
      base: weeklyBonus,
      achievement: 0,
      stretch: 0,
      streak: 0
    },
    celebrationMessage,
    achievementLevel
  };
}

export function calculatePotentialBonus(
  currentWeekPoints: number,
  weeklyGoal: number,
  baselineGoal: number
): { currentBonus: number; potentialBonus: number; remainingPoints: number } {
  const current = calculateWeeklyBonus(currentWeekPoints, weeklyGoal, baselineGoal);
  const potential = calculateWeeklyBonus(weeklyGoal, weeklyGoal, baselineGoal);
  
  return {
    currentBonus: current.activityCreditsEarned,
    potentialBonus: potential.activityCreditsEarned,
    remainingPoints: Math.max(0, weeklyGoal - currentWeekPoints)
  };
}